<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <div class="px-4 pb-4">
    <PrimeCard>
      <template #title>
        <span v-if="isNewPlan">{{
          $t("adminClassificationPage.titleNew")
        }}</span>
        <span v-else>{{ $t("adminClassificationPage.titleAdmin") }}</span>
      </template>
      <template #subtitle>
        <div class="flex align-items-center justify-content-between">
          <span
            >{{ $t("adminClassificationPage.file") }} :
            <code>{{ filename }}</code></span
          >
          <PrimeButton
            v-if="!loading && planContent"
            :icon="showFilters ? 'ri-filter-off-line' : 'ri-filter-line'"
            :label="$t('adminClassificationPage.toggleFilters')"
            severity="secondary"
            text
            size="small"
            @click="showFilters = !showFilters"
          />
        </div>
      </template>

      <template #content>
        <div v-if="loading" class="text-center">
          <i class="pi pi-spin pi-spinner" style="font-size: 2rem"></i>
          <p>{{ $t("adminClassificationPage.loading") }}</p>
        </div>

        <div v-if="!loading && planContent">
          <PrimeTabs :value="0">
            <PrimeTabList>
              <PrimeTab :value="0">{{
                $t("adminClassificationPage.tabs.natures")
              }}</PrimeTab>
              <PrimeTab :value="1">{{
                $t("adminClassificationPage.tabs.fields")
              }}</PrimeTab>
              <PrimeTab :value="2">{{
                $t("adminClassificationPage.tabs.emplacements")
              }}</PrimeTab>
              <PrimeTab :value="3">{{
                $t("adminClassificationPage.tabs.categories")
              }}</PrimeTab>
              <PrimeTab :value="4">{{
                $t("adminClassificationPage.tabs.dictionaries")
              }}</PrimeTab>
              <PrimeTab :value="5">{{
                $t("adminClassificationPage.tabs.lexicons")
              }}</PrimeTab>
            </PrimeTabList>
            <PrimeTabPanels>
              <!-- Natures Tab -->
              <PrimeTabPanel :value="0">
                <div v-if="canEdit" class="mb-2">
                  <PrimeButton
                    :label="$t('adminClassificationPage.buttons.newNature')"
                    icon="ri-add-line"
                    @click="openNewNatureDialog"
                  />
                </div>
                <PrimeDataTable
                  :value="planContent.NATURES"
                  v-model:filters="naturesFilters"
                  :filterDisplay="showFilters ? 'row' : undefined"
                  paginator
                  :rows="10"
                  :rowsPerPageOptions="[10, 25, 50]"
                  paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                  currentPageReportTemplate="{first} - {last} / {totalRecords}"
                >
                  <PrimeColumn
                    field="code"
                    :header="$t('adminClassificationPage.columns.code')"
                    sortable
                    :showFilterMenu="false"
                  >
                    <template #filter="{ filterModel, filterCallback }">
                      <PrimeInputText
                        v-model="filterModel.value"
                        @input="filterCallback()"
                        :placeholder="
                          $t('adminClassificationPage.filterPlaceholder')
                        "
                        class="w-full"
                        size="small"
                      />
                    </template>
                  </PrimeColumn>
                  <PrimeColumn
                    field="name"
                    :header="$t('adminClassificationPage.columns.name')"
                    sortable
                    :showFilterMenu="false"
                  >
                    <template #filter="{ filterModel, filterCallback }">
                      <PrimeInputText
                        v-model="filterModel.value"
                        @input="filterCallback()"
                        :placeholder="
                          $t('adminClassificationPage.filterPlaceholder')
                        "
                        class="w-full"
                        size="small"
                      />
                    </template>
                  </PrimeColumn>
                  <PrimeColumn
                    :header="$t('adminClassificationPage.columns.emplacement')"
                  >
                    <template #body="slotProps">
                      <code>{{ getCalculatedPath(slotProps.data) }}</code>
                    </template>
                  </PrimeColumn>
                  <PrimeColumn
                    field="type"
                    :header="$t('adminClassificationPage.columns.type')"
                    :showFilterMenu="false"
                  >
                    <template #filter="{ filterModel, filterCallback }">
                      <PrimeInputText
                        v-model="filterModel.value"
                        @input="filterCallback()"
                        :placeholder="
                          $t('adminClassificationPage.filterPlaceholder')
                        "
                        class="w-full"
                        size="small"
                      />
                    </template>
                  </PrimeColumn>
                  <PrimeColumn
                    :header="$t('adminClassificationPage.columns.icon')"
                  >
                    <template #body="slotProps">
                      <i :class="slotProps.data.icon" aria-hidden="true"></i>
                    </template>
                  </PrimeColumn>
                  <PrimeColumn
                    :header="$t('adminClassificationPage.columns.categories')"
                  >
                    <template #body="slotProps">
                      <PrimeTag
                        v-for="catId in slotProps.data.categories || []"
                        :key="catId"
                        :value="getCategoryLabel(catId)"
                        class="mr-1"
                      />
                    </template>
                  </PrimeColumn>
                  <PrimeColumn
                    v-if="canEdit"
                    :header="$t('adminClassificationPage.columns.actions')"
                  >
                    <template #body="slotProps">
                      <PrimeButton
                        icon="ri-pencil-line"
                        :aria-label="$t('modify')"
                        rounded
                        severity="success"
                        class="mr-2"
                        @click="openEditNatureDialog(slotProps.data)"
                      />
                      <PrimeButton
                        icon="ri-file-copy-line"
                        :aria-label="$t('duplicate')"
                        rounded
                        severity="info"
                        class="mr-2"
                        @click="openDuplicateNatureDialog(slotProps.data)"
                      />
                      <PrimeButton
                        icon="ri-delete-bin-line"
                        :aria-label="$t('delete')"
                        rounded
                        severity="warn"
                        @click="confirmDeleteNature(slotProps.data)"
                      />
                    </template>
                  </PrimeColumn>
                </PrimeDataTable>
              </PrimeTabPanel>

              <!-- Fields Tab -->
              <PrimeTabPanel :value="1">
                <div v-if="canEdit" class="mb-2">
                  <PrimeButton
                    :label="$t('adminClassificationPage.buttons.newField')"
                    icon="ri-add-line"
                    @click="openNewFieldDialog"
                  />
                </div>
                <PrimeDataTable
                  :value="planContent.FIELDS"
                  v-model:filters="fieldsFilters"
                  :filterDisplay="showFilters ? 'row' : undefined"
                  paginator
                  :rows="10"
                  :rowsPerPageOptions="[10, 25, 50]"
                  paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                  currentPageReportTemplate="{first} - {last} / {totalRecords}"
                >
                  <PrimeColumn
                    field="name"
                    :header="$t('adminClassificationPage.columns.name')"
                    sortable
                    :showFilterMenu="false"
                  >
                    <template #filter="{ filterModel, filterCallback }">
                      <PrimeInputText
                        v-model="filterModel.value"
                        @input="filterCallback()"
                        :placeholder="
                          $t('adminClassificationPage.filterPlaceholder')
                        "
                        class="w-full"
                        size="small"
                      />
                    </template>
                  </PrimeColumn>
                  <PrimeColumn
                    field="description"
                    :header="$t('adminClassificationPage.columns.description')"
                    :showFilterMenu="false"
                  >
                    <template #filter="{ filterModel, filterCallback }">
                      <PrimeInputText
                        v-model="filterModel.value"
                        @input="filterCallback()"
                        :placeholder="
                          $t('adminClassificationPage.filterPlaceholder')
                        "
                        class="w-full"
                        size="small"
                      />
                    </template>
                  </PrimeColumn>
                  <PrimeColumn
                    field="type"
                    :header="$t('adminClassificationPage.columns.type')"
                    :showFilterMenu="false"
                  >
                    <template #filter="{ filterModel, filterCallback }">
                      <PrimeInputText
                        v-model="filterModel.value"
                        @input="filterCallback()"
                        :placeholder="
                          $t('adminClassificationPage.filterPlaceholder')
                        "
                        class="w-full"
                        size="small"
                      />
                    </template>
                  </PrimeColumn>
                  <PrimeColumn
                    :header="$t('adminClassificationPage.columns.status')"
                  >
                    <template #body="slotProps">
                      <PrimeTag
                        v-if="slotProps.data.optional === true"
                        :value="$t('adminClassificationPage.columns.optional')"
                        severity="info"
                      />
                    </template>
                  </PrimeColumn>
                  <PrimeColumn
                    v-if="canEdit"
                    :header="$t('adminClassificationPage.columns.actions')"
                  >
                    <template #body="slotProps">
                      <PrimeButton
                        icon="ri-pencil-line"
                        :aria-label="$t('modify')"
                        rounded
                        severity="success"
                        class="mr-2"
                        @click="openEditFieldDialog(slotProps.data)"
                      />
                      <PrimeButton
                        icon="ri-file-copy-line"
                        :aria-label="$t('duplicate')"
                        rounded
                        severity="info"
                        class="mr-2"
                        @click="openDuplicateFieldDialog(slotProps.data)"
                      />
                      <PrimeButton
                        icon="ri-delete-bin-line"
                        :aria-label="$t('delete')"
                        rounded
                        severity="warn"
                        @click="confirmDeleteField(slotProps.data)"
                      />
                    </template>
                  </PrimeColumn>
                </PrimeDataTable>
              </PrimeTabPanel>

              <!-- Emplacements Tab -->
              <PrimeTabPanel :value="2">
                <div v-if="canEdit" class="mb-2">
                  <PrimeButton
                    :label="
                      $t('adminClassificationPage.buttons.newEmplacement')
                    "
                    icon="ri-add-line"
                    @click="openNewEmplacementDialog"
                  />
                </div>
                <PrimeDataTable
                  :value="planContent.EMPLACEMENT"
                  v-model:filters="emplacementsFilters"
                  :filterDisplay="showFilters ? 'row' : undefined"
                  paginator
                  :rows="10"
                  :rowsPerPageOptions="[10, 25, 50]"
                  paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                  currentPageReportTemplate="{first} - {last} / {totalRecords}"
                >
                  <PrimeColumn
                    field="code"
                    :header="$t('adminClassificationPage.columns.code')"
                    sortable
                    :showFilterMenu="false"
                  >
                    <template #filter="{ filterModel, filterCallback }">
                      <PrimeInputText
                        v-model="filterModel.value"
                        @input="filterCallback()"
                        :placeholder="
                          $t('adminClassificationPage.filterPlaceholder')
                        "
                        class="w-full"
                        size="small"
                      />
                    </template>
                  </PrimeColumn>
                  <PrimeColumn
                    :header="$t('adminClassificationPage.columns.name')"
                  >
                    <template #body="slotProps">
                      {{
                        slotProps.data.metadata?.["cm:name"]?.value ||
                        slotProps.data.metadata?.["cm:name"]?.template ||
                        ""
                      }}
                    </template>
                  </PrimeColumn>
                  <PrimeColumn
                    v-if="canEdit"
                    :header="$t('adminClassificationPage.columns.actions')"
                  >
                    <template #body="slotProps">
                      <PrimeButton
                        icon="ri-pencil-line"
                        :aria-label="$t('modify')"
                        rounded
                        severity="success"
                        class="mr-2"
                        @click="openEditEmplacementDialog(slotProps.data)"
                      />
                      <PrimeButton
                        icon="ri-delete-bin-line"
                        :aria-label="$t('delete')"
                        rounded
                        severity="warn"
                        @click="confirmDeleteEmplacement(slotProps.data)"
                      />
                    </template>
                  </PrimeColumn>
                </PrimeDataTable>
              </PrimeTabPanel>

              <!-- Categories Tab -->
              <PrimeTabPanel :value="3">
                <div v-if="canEdit" class="mb-2">
                  <PrimeButton
                    :label="$t('adminClassificationPage.buttons.newCategory')"
                    icon="ri-add-line"
                    @click="openNewCategoryDialog"
                  />
                </div>
                <PrimeDataTable
                  :value="planContent.VIEW_CATEGORIES"
                  v-model:filters="categoriesFilters"
                  :filterDisplay="showFilters ? 'row' : undefined"
                  @row-reorder="onCategoryReorder"
                  paginator
                  :rows="10"
                  :rowsPerPageOptions="[10, 25, 50]"
                  paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                  currentPageReportTemplate="{first} - {last} / {totalRecords}"
                  :reorderable-rows="canEdit"
                >
                  <PrimeColumn
                    v-if="canEdit"
                    rowReorder
                    headerStyle="width: 3rem"
                    :reorderable-column="false"
                  />
                  <PrimeColumn
                    field="id"
                    :header="$t('adminClassificationPage.columns.id')"
                    sortable
                    :showFilterMenu="false"
                  >
                    <template #filter="{ filterModel, filterCallback }">
                      <PrimeInputText
                        v-model="filterModel.value"
                        @input="filterCallback()"
                        :placeholder="
                          $t('adminClassificationPage.filterPlaceholder')
                        "
                        class="w-full"
                        size="small"
                      />
                    </template>
                  </PrimeColumn>
                  <PrimeColumn
                    field="label"
                    :header="$t('adminClassificationPage.columns.label')"
                    :showFilterMenu="false"
                  >
                    <template #filter="{ filterModel, filterCallback }">
                      <PrimeInputText
                        v-model="filterModel.value"
                        @input="filterCallback()"
                        :placeholder="
                          $t('adminClassificationPage.filterPlaceholder')
                        "
                        class="w-full"
                        size="small"
                      />
                    </template>
                  </PrimeColumn>
                  <PrimeColumn
                    :header="$t('adminClassificationPage.columns.display')"
                  >
                    <template #body="slotProps">
                      <PrimeTag
                        v-for="d in slotProps.data.display"
                        :key="d"
                        :value="d"
                        class="mr-1"
                      />
                    </template>
                  </PrimeColumn>
                  <PrimeColumn
                    :header="$t('adminClassificationPage.columns.views')"
                  >
                    <template #body="slotProps">
                      <div class="flex align-items-center gap-2">
                        <PrimeTag
                          v-if="
                            !slotProps.data.views ||
                            slotProps.data.views.length === 0
                          "
                          :value="
                            $t('adminClassificationPage.viewSelector.noViews')
                          "
                          severity="secondary"
                        />
                        <PrimeTag
                          v-else
                          :value="
                            $t('adminClassificationPage.viewSelector.nViews', {
                              count: slotProps.data.views.length,
                            })
                          "
                          severity="info"
                        />
                        <PrimeButton
                          :icon="canEdit ? 'ri-pencil-line' : 'ri-eye-line'"
                          size="small"
                          severity="secondary"
                          text
                          rounded
                          @click="
                            openViewSelector(slotProps.data, slotProps.index)
                          "
                        />
                      </div>
                    </template>
                  </PrimeColumn>
                  <PrimeColumn
                    v-if="canEdit"
                    :header="$t('adminClassificationPage.columns.actions')"
                  >
                    <template #body="slotProps">
                      <PrimeButton
                        icon="ri-pencil-line"
                        :aria-label="$t('modify')"
                        rounded
                        severity="success"
                        class="mr-2"
                        @click="openEditCategoryDialog(slotProps.data)"
                      />
                      <PrimeButton
                        icon="ri-delete-bin-line"
                        :aria-label="$t('delete')"
                        rounded
                        severity="warn"
                        @click="confirmDeleteCategory(slotProps.data)"
                      />
                    </template>
                  </PrimeColumn>
                  <template #empty>
                    {{ $t("adminClassificationPage.tabs.categories") }} : 0
                  </template>
                </PrimeDataTable>
              </PrimeTabPanel>
              <!-- Dictionaries Tab -->
              <PrimeTabPanel :value="4">
                <div v-if="canEdit" class="mb-2">
                  <PrimeButton
                    :label="$t('adminClassificationPage.buttons.newDictionary')"
                    icon="ri-add-line"
                    @click="openNewDictionaryDialog"
                  />
                </div>
                <PrimeDataTable
                  :value="planContent.DICTIONARIES"
                  v-model:filters="dictionariesFilters"
                  :filterDisplay="showFilters ? 'row' : undefined"
                  paginator
                  :rows="10"
                  :rowsPerPageOptions="[10, 25, 50]"
                  paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                  currentPageReportTemplate="{first} - {last} / {totalRecords}"
                >
                  <PrimeColumn
                    field="id"
                    :header="$t('adminClassificationPage.columns.id')"
                    sortable
                    :showFilterMenu="false"
                  >
                    <template #filter="{ filterModel, filterCallback }">
                      <PrimeInputText
                        v-model="filterModel.value"
                        @input="filterCallback()"
                        :placeholder="
                          $t('adminClassificationPage.filterPlaceholder')
                        "
                        class="w-full"
                        size="small"
                      />
                    </template>
                  </PrimeColumn>
                  <PrimeColumn
                    field="label"
                    :header="$t('adminClassificationPage.columns.label')"
                    :showFilterMenu="false"
                  >
                    <template #filter="{ filterModel, filterCallback }">
                      <PrimeInputText
                        v-model="filterModel.value"
                        @input="filterCallback()"
                        :placeholder="
                          $t('adminClassificationPage.filterPlaceholder')
                        "
                        class="w-full"
                        size="small"
                      />
                    </template>
                  </PrimeColumn>
                  <PrimeColumn
                    :header="$t('adminClassificationPage.columns.entriesCount')"
                  >
                    <template #body="slotProps">
                      {{ (slotProps.data.entries || []).length }}
                    </template>
                  </PrimeColumn>
                  <PrimeColumn
                    v-if="canEdit"
                    :header="$t('adminClassificationPage.columns.actions')"
                  >
                    <template #body="slotProps">
                      <PrimeButton
                        icon="ri-pencil-line"
                        :aria-label="$t('modify')"
                        rounded
                        severity="success"
                        class="mr-2"
                        @click="openEditDictionaryDialog(slotProps.data)"
                      />
                      <PrimeButton
                        icon="ri-delete-bin-line"
                        :aria-label="$t('delete')"
                        rounded
                        severity="warn"
                        @click="confirmDeleteDictionary(slotProps.data)"
                      />
                    </template>
                  </PrimeColumn>
                  <template #empty>
                    {{ $t("adminClassificationPage.tabs.dictionaries") }}
                    : 0
                  </template>
                </PrimeDataTable>
              </PrimeTabPanel>

              <!-- Lexicons Tab -->
              <PrimeTabPanel :value="5">
                <div v-if="canEdit" class="mb-2">
                  <PrimeButton
                    :label="$t('adminClassificationPage.buttons.newLexicon')"
                    icon="ri-add-line"
                    @click="openNewLexiconDialog"
                  />
                </div>
                <PrimeDataTable
                  :value="planContent.LEXICONS"
                  v-model:filters="lexiconsFilters"
                  :filterDisplay="showFilters ? 'row' : undefined"
                  paginator
                  :rows="10"
                  :rowsPerPageOptions="[10, 25, 50]"
                  paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                  currentPageReportTemplate="{first} - {last} / {totalRecords}"
                >
                  <PrimeColumn
                    field="id"
                    :header="$t('adminClassificationPage.columns.id')"
                    sortable
                    :showFilterMenu="false"
                  >
                    <template #filter="{ filterModel, filterCallback }">
                      <PrimeInputText
                        v-model="filterModel.value"
                        @input="filterCallback()"
                        :placeholder="
                          $t('adminClassificationPage.filterPlaceholder')
                        "
                        class="w-full"
                        size="small"
                      />
                    </template>
                  </PrimeColumn>
                  <PrimeColumn
                    field="label"
                    :header="$t('adminClassificationPage.columns.label')"
                    :showFilterMenu="false"
                  >
                    <template #filter="{ filterModel, filterCallback }">
                      <PrimeInputText
                        v-model="filterModel.value"
                        @input="filterCallback()"
                        :placeholder="
                          $t('adminClassificationPage.filterPlaceholder')
                        "
                        class="w-full"
                        size="small"
                      />
                    </template>
                  </PrimeColumn>
                  <PrimeColumn
                    :header="$t('adminClassificationPage.columns.valuesCount')"
                  >
                    <template #body="slotProps">
                      {{ (slotProps.data.values || []).length }}
                    </template>
                  </PrimeColumn>
                  <PrimeColumn
                    v-if="canEdit"
                    :header="$t('adminClassificationPage.columns.actions')"
                  >
                    <template #body="slotProps">
                      <PrimeButton
                        icon="ri-pencil-line"
                        :aria-label="$t('modify')"
                        rounded
                        severity="success"
                        class="mr-2"
                        @click="openEditLexiconDialog(slotProps.data)"
                      />
                      <PrimeButton
                        icon="ri-delete-bin-line"
                        :aria-label="$t('delete')"
                        rounded
                        severity="warn"
                        @click="confirmDeleteLexicon(slotProps.data)"
                      />
                    </template>
                  </PrimeColumn>
                  <template #empty>
                    {{ $t("adminClassificationPage.tabs.lexicons") }} : 0
                  </template>
                </PrimeDataTable>
              </PrimeTabPanel>
            </PrimeTabPanels>
          </PrimeTabs>
        </div>
      </template>

      <template #footer>
        <div class="flex gap-2 justify-content-between">
          <div class="flex gap-2">
            <PrimeButton
              :label="$t('adminClassificationPage.buttons.export')"
              icon="ri-download-line"
              :disabled="loading || !planContent"
              @click="exportPlan"
              severity="secondary"
              outlined
            />
            <PrimeButton
              v-if="canEdit"
              :label="$t('adminClassificationPage.buttons.import')"
              icon="ri-upload-line"
              :disabled="loading || isSaving"
              @click="triggerImport"
              severity="secondary"
              outlined
            />
          </div>
          <div class="flex gap-2">
            <PrimeButton
              :label="$t('adminClassificationPage.buttons.reload')"
              icon="pi pi-refresh"
              :disabled="loading || isSaving"
              @click="loadPlan"
              severity="secondary"
            />
            <PrimeButton
              v-if="canEdit"
              :label="
                isNewPlan
                  ? $t('adminClassificationPage.buttons.createFile')
                  : $t('adminClassificationPage.buttons.saveFile')
              "
              icon="pi pi-save"
              :loading="isSaving"
              :disabled="loading || !isDirty"
              class="w-18rem"
              @click="savePlanFile"
            />
          </div>
        </div>
        <input
          ref="fileInput"
          type="file"
          accept=".json"
          style="display: none"
          @change="handleFileImport"
        />
      </template>
    </PrimeCard>

    <!-- Dialogs -->
    <PrimeDialog
      v-model:visible="displayNatureDialog"
      :header="dialogTitle"
      modal
      class="w-full"
      style="width: 50rem"
    >
      <NatureForm
        ref="natureForm"
        :nature="selectedItem"
        :existing-natures="planContent.NATURES || []"
        :emplacements="planContent.EMPLACEMENT || []"
        :categories="planContent.VIEW_CATEGORIES || []"
        :fields="planContent.FIELDS || []"
        :is-duplicating="isDuplicating"
        @save="onNatureSaved"
      />
      <template #footer>
        <PrimeButton
          :label="$t('adminClassificationPage.buttons.cancel')"
          icon="pi pi-times"
          text
          @click="displayNatureDialog = false"
        />
        <PrimeButton
          :label="$t('adminClassificationPage.buttons.save')"
          icon="pi pi-check"
          @click="$refs.natureForm.submit()"
        />
      </template>
    </PrimeDialog>

    <PrimeDialog
      v-model:visible="displayFieldDialog"
      :header="dialogTitle"
      modal
      class="w-full"
      style="width: 50rem"
    >
      <FieldForm
        ref="fieldForm"
        :field="selectedItem"
        :existing-fields="planContent.FIELDS || []"
        :dictionaries="planContent.DICTIONARIES || []"
        :lexicons="planContent.LEXICONS || []"
        @save="onFieldSaved"
      />
      <template #footer>
        <PrimeButton
          :label="$t('adminClassificationPage.buttons.cancel')"
          icon="pi pi-times"
          text
          @click="displayFieldDialog = false"
        />
        <PrimeButton
          :label="$t('adminClassificationPage.buttons.save')"
          icon="pi pi-check"
          @click="$refs.fieldForm.submit()"
        />
      </template>
    </PrimeDialog>

    <PrimeDialog
      v-model:visible="displayEmplacementDialog"
      :header="dialogTitle"
      modal
      class="w-full"
      style="width: 50rem"
    >
      <EmplacementForm
        ref="emplacementForm"
        :emplacement="selectedItem"
        :fields="planContent.FIELDS || []"
        @save="onEmplacementSaved"
      />
      <template #footer>
        <PrimeButton
          :label="$t('adminClassificationPage.buttons.cancel')"
          icon="pi pi-times"
          text
          @click="displayEmplacementDialog = false"
        />
        <PrimeButton
          :label="$t('adminClassificationPage.buttons.save')"
          icon="pi pi-check"
          @click="$refs.emplacementForm.submit()"
        />
      </template>
    </PrimeDialog>

    <PrimeDialog
      v-model:visible="displayCategoryDialog"
      :header="dialogTitle"
      modal
      class="w-full"
      style="width: 50rem"
    >
      <div class="flex flex-column gap-3 p-3">
        <div class="category-form-field">
          <label class="category-form-label" for="categoryId">{{
            $t("adminClassificationPage.columns.id")
          }}</label>
          <PrimeInputText
            id="categoryId"
            v-model="categoryForm.id"
            :disabled="isEditingCategory"
            class="w-full"
          />
        </div>
        <div class="category-form-field">
          <label class="category-form-label" for="categoryLabel">{{
            $t("adminClassificationPage.columns.label")
          }}</label>
          <PrimeInputText
            id="categoryLabel"
            v-model="categoryForm.label"
            class="w-full"
          />
        </div>
        <div class="category-form-field">
          <label class="category-form-label" for="categoryDisplay">{{
            $t("adminClassificationPage.columns.display")
          }}</label>
          <PrimeMultiSelect
            id="categoryDisplay"
            v-model="categoryForm.display"
            :options="displayOptions"
            option-label="label"
            option-value="value"
            class="w-full"
          />
        </div>
      </div>
      <template #footer>
        <PrimeButton
          :label="$t('adminClassificationPage.buttons.cancel')"
          icon="pi pi-times"
          text
          @click="displayCategoryDialog = false"
        />
        <PrimeButton
          :label="$t('adminClassificationPage.buttons.save')"
          icon="pi pi-check"
          @click="saveCategoryForm"
        />
      </template>
    </PrimeDialog>

    <ViewSelectorPopup
      :visible="displayViewSelector"
      @update:visible="displayViewSelector = $event"
      :selectedViews="
        viewSelectorCategory ? viewSelectorCategory.views || [] : []
      "
      :availableViews="availableViews"
      :loading="viewsLoading"
      :readonly="!canEdit"
      @update:selectedViews="onViewsUpdated"
    />

    <PrimeDialog
      v-model:visible="displayDictionaryDialog"
      :header="dialogTitle"
      modal
      class="w-full"
      style="width: 50rem"
    >
      <DictionaryForm
        ref="dictionaryForm"
        :dictionary="selectedItem"
        :existing-dictionaries="
          planContent ? planContent.DICTIONARIES || [] : []
        "
        @save="onDictionarySaved"
      />
      <template #footer>
        <PrimeButton
          :label="$t('adminClassificationPage.buttons.cancel')"
          icon="pi pi-times"
          text
          @click="displayDictionaryDialog = false"
        />
        <PrimeButton
          :label="$t('adminClassificationPage.buttons.save')"
          icon="pi pi-check"
          @click="$refs.dictionaryForm.submit()"
        />
      </template>
    </PrimeDialog>

    <PrimeDialog
      v-model:visible="displayLexiconDialog"
      :header="dialogTitle"
      modal
      class="w-full"
      style="width: 50rem"
    >
      <LexiconForm
        ref="lexiconForm"
        :lexicon="selectedItem"
        :existing-lexicons="planContent ? planContent.LEXICONS || [] : []"
        @save="onLexiconSaved"
      />
      <template #footer>
        <PrimeButton
          :label="$t('adminClassificationPage.buttons.cancel')"
          icon="pi pi-times"
          text
          @click="displayLexiconDialog = false"
        />
        <PrimeButton
          :label="$t('adminClassificationPage.buttons.save')"
          icon="pi pi-check"
          @click="$refs.lexiconForm.submit()"
        />
      </template>
    </PrimeDialog>

    <PrimeConfirmDialog group="deleteItem" />
  </div>
</template>

<script>
import Card from "primevue/card";
import Button from "primevue/button";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Dialog from "primevue/dialog";
import ConfirmDialog from "primevue/confirmdialog";
import Tabs from "primevue/tabs";
import TabList from "primevue/tablist";
import Tab from "primevue/tab";
import TabPanels from "primevue/tabpanels";
import TabPanel from "primevue/tabpanel";
import InputText from "primevue/inputtext";
import MultiSelect from "primevue/multiselect";
import Tag from "primevue/tag";
import { useConfirm } from "primevue/useconfirm";

import { FilterMatchMode } from "@primevue/core/api";
import classificationService from "@/services/ClassificationService.js";
import viewConfigService from "@/services/ViewConfigService.js";
import { extractVariables } from "@/utils/classificationPlanUtils.js";
import { useI18n } from "vue-i18n";
import NatureForm from "./NatureForm.vue";
import FieldForm from "./FieldForm.vue";
import EmplacementForm from "./EmplacementForm.vue";
import ViewSelectorPopup from "./ViewSelectorPopup.vue";
import DictionaryForm from "./DictionaryForm.vue";
import LexiconForm from "./LexiconForm.vue";

const defaultPlanTemplate = () => ({
  NAME: "Nouveau Plan",
  DESCRIPTION: "Description",
  FIELDS: [],
  EMPLACEMENT: [],
  NATURES: [],
  VIEW_CATEGORIES: [],
  DICTIONARIES: [],
  LEXICONS: [],
});

export default {
  name: "AdminClassificationPage",
  components: {
    PrimeCard: Card,
    PrimeButton: Button,
    PrimeDataTable: DataTable,
    PrimeColumn: Column,
    PrimeDialog: Dialog,
    PrimeConfirmDialog: ConfirmDialog,
    PrimeTabs: Tabs,
    PrimeTabList: TabList,
    PrimeTab: Tab,
    PrimeTabPanels: TabPanels,
    PrimeTabPanel: TabPanel,
    PrimeInputText: InputText,
    PrimeMultiSelect: MultiSelect,
    PrimeTag: Tag,
    NatureForm,
    FieldForm,
    EmplacementForm,
    ViewSelectorPopup,
    DictionaryForm,
    LexiconForm,
  },
  props: {
    filename: { type: String, required: true },
    extraDisplayOptions: { type: Array, default: () => [] },
  },
  setup() {
    const confirm = useConfirm();
    const { t } = useI18n();
    return { confirm, t };
  },
  data() {
    return {
      planContent: null,
      loading: true,
      isSaving: false,
      isNewPlan: false,
      dialogTitle: "",
      selectedItem: null,
      displayNatureDialog: false,
      displayFieldDialog: false,
      displayEmplacementDialog: false,
      isDirty: false,
      isDuplicating: false,
      displayCategoryDialog: false,
      displayDictionaryDialog: false,
      displayLexiconDialog: false,
      categoryForm: { id: "", label: "", display: [], views: [] },
      isEditingCategory: false,
      defaultDisplayOptions: [
        { label: "folderView", value: "folderView" },
        { label: "import", value: "import" },
        { label: "requalify", value: "requalify" },
      ],
      availableViews: [],
      viewsLoading: false,
      displayViewSelector: false,
      viewSelectorCategory: null,
      canEdit: false,
      showFilters: false,
      naturesFilters: {
        code: { value: null, matchMode: FilterMatchMode.CONTAINS },
        name: { value: null, matchMode: FilterMatchMode.CONTAINS },
        type: { value: null, matchMode: FilterMatchMode.CONTAINS },
      },
      fieldsFilters: {
        name: { value: null, matchMode: FilterMatchMode.CONTAINS },
        description: { value: null, matchMode: FilterMatchMode.CONTAINS },
        type: { value: null, matchMode: FilterMatchMode.CONTAINS },
      },
      emplacementsFilters: {
        code: { value: null, matchMode: FilterMatchMode.CONTAINS },
      },
      categoriesFilters: {
        id: { value: null, matchMode: FilterMatchMode.CONTAINS },
        label: { value: null, matchMode: FilterMatchMode.CONTAINS },
      },
      dictionariesFilters: {
        id: { value: null, matchMode: FilterMatchMode.CONTAINS },
        label: { value: null, matchMode: FilterMatchMode.CONTAINS },
      },
      lexiconsFilters: {
        id: { value: null, matchMode: FilterMatchMode.CONTAINS },
        label: { value: null, matchMode: FilterMatchMode.CONTAINS },
      },
    };
  },
  computed: {
    displayOptions() {
      return [...this.defaultDisplayOptions, ...this.extraDisplayOptions];
    },
  },
  methods: {
    getCalculatedPath(nature) {
      if (!nature.emplacement || nature.emplacement.length === 0) {
        return "/";
      }
      return (
        "/" +
        nature.emplacement
          .map((code) => {
            const emp = this.planContent.EMPLACEMENT.find(
              (e) => e.code === code,
            );
            if (!emp) {
              return `[${code}]`;
            }
            const cmName = emp.metadata?.["cm:name"];
            return cmName?.value || cmName?.template || code;
          })
          .join("/")
      );
    },
    getCategoryLabel(catId) {
      const cat = (this.planContent?.VIEW_CATEGORIES || []).find(
        (c) => c.id === catId,
      );
      return cat ? cat.label : catId;
    },
    loadPlan() {
      this.loading = true;
      this.isNewPlan = false;
      this.isDirty = false;
      classificationService
        .getPlanWithPermissions(this.filename)
        .then(({ content, allowableOperations }) => {
          if (!content.DICTIONARIES) {
            content.DICTIONARIES = [];
          }
          if (!content.LEXICONS) {
            content.LEXICONS = [];
          }
          this.planContent = content;
          this.canEdit = allowableOperations?.includes("update") ?? false;
        })
        .catch((err) => {
          if (err.response && err.response.status === 404) {
            this.isNewPlan = true;
            this.canEdit = true;
            this.planContent = defaultPlanTemplate();
            this.isDirty = true; // New plan is dirty by default
            this.$toast.add({
              severity: "info",
              summary: this.t("adminClassificationPage.toast.newPlan"),
              detail: this.t("adminClassificationPage.toast.fileNotExist", {
                filename: this.filename,
              }),
              life: 3000,
            });
          } else {
            this.$toast.add({
              severity: "error",
              summary: this.t("adminClassificationPage.toast.loadError"),
              detail: this.t("adminClassificationPage.toast.loadErrorDetail"),
              life: 4000,
            });
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    savePlanFile() {
      this.isSaving = true;
      classificationService
        .savePlan(this.filename, this.planContent)
        .then(() => {
          this.$toast.add({
            severity: "success",
            summary: this.t("success"),
            detail: this.t("adminClassificationPage.toast.saveSuccess"),
            life: 3000,
          });
          this.isNewPlan = false;
          this.isDirty = false;
        })
        .catch(() => {
          this.$toast.add({
            severity: "error",
            summary: this.t("error"),
            detail: this.t("adminClassificationPage.toast.saveError"),
            life: 4000,
          });
        })
        .finally(() => {
          this.isSaving = false;
        });
    },
    handleBeforeUnload(event) {
      if (this.isDirty) {
        event.preventDefault();
        event.returnValue = "";
      }
    },
    exportPlan() {
      if (!this.planContent) {
        return;
      }

      const dataStr = JSON.stringify(this.planContent, null, 2);
      const dataBlob = new Blob([dataStr], { type: "application/json" });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = this.filename.endsWith(".json")
        ? this.filename
        : `${this.filename}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      this.$toast.add({
        severity: "success",
        summary: this.t("adminClassificationPage.toast.exportSuccess"),
        detail: this.t("adminClassificationPage.toast.exportSuccessDetail", {
          filename: this.filename,
        }),
        life: 3000,
      });
    },
    triggerImport() {
      this.$refs.fileInput.click();
    },
    handleFileImport(event) {
      const file = event.target.files[0];
      if (!file) {
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const importedData = JSON.parse(e.target.result);

          // Validate that imported data has the expected structure
          if (
            !importedData.NATURES ||
            !importedData.FIELDS ||
            !importedData.EMPLACEMENT
          ) {
            throw new Error(
              this.t("adminClassificationPage.toast.invalidFileStructure"),
            );
          }

          this.planContent = importedData;
          this.isDirty = true;
          this.$toast.add({
            severity: "success",
            summary: this.t("adminClassificationPage.toast.importSuccess"),
            detail: this.t("adminClassificationPage.toast.importSuccessDetail"),
            life: 4000,
          });
        } catch (error) {
          this.$toast.add({
            severity: "error",
            summary: this.t("adminClassificationPage.toast.importError"),
            detail: this.t("adminClassificationPage.toast.importErrorDetail", {
              message: error.message,
            }),
            life: 5000,
          });
        }
      };
      reader.readAsText(file);

      // Reset input value to allow importing the same file again
      event.target.value = "";
    },

    async loadAvailableViews() {
      this.viewsLoading = true;
      try {
        const filenames = await viewConfigService.listViews();
        this.availableViews = filenames.map((f) => ({
          label: f,
          value: f,
        }));
      } catch {
        this.availableViews = [];
      } finally {
        this.viewsLoading = false;
      }
    },

    // Nature Methods
    openNewNatureDialog() {
      this.selectedItem = null;
      this.isDuplicating = false;
      this.dialogTitle = this.t("adminClassificationPage.dialog.createNature");
      this.displayNatureDialog = true;
    },
    openEditNatureDialog(item) {
      this.selectedItem = item;
      this.isDuplicating = false;
      this.dialogTitle = this.t("adminClassificationPage.dialog.editNature", {
        name: item.name,
      });
      this.displayNatureDialog = true;
    },
    openDuplicateNatureDialog(item) {
      this.selectedItem = JSON.parse(JSON.stringify(item));
      this.selectedItem.code = "";
      this.isDuplicating = true;
      this.dialogTitle = this.t(
        "adminClassificationPage.dialog.duplicateNature",
        {
          name: item.name,
        },
      );
      this.displayNatureDialog = true;
    },
    onNatureSaved(itemData) {
      const index = this.planContent.NATURES.findIndex(
        (i) => i.code === itemData.code,
      );
      if (index !== -1) {
        this.planContent.NATURES[index] = itemData;
      } else {
        this.planContent.NATURES.push(itemData);
      }
      this.autocreateFieldsFromNature(itemData);
      this.displayNatureDialog = false;
    },
    confirmDeleteNature(item) {
      this.confirmDeleteItem(
        item,
        this.planContent.NATURES,
        this.t("adminClassificationPage.confirm.nature"),
      );
    },

    // Field Methods
    openNewFieldDialog() {
      this.selectedItem = null;
      this.dialogTitle = this.t("adminClassificationPage.dialog.createField");
      this.displayFieldDialog = true;
    },
    openEditFieldDialog(item) {
      this.selectedItem = item;
      this.dialogTitle = this.t("adminClassificationPage.dialog.editField", {
        name: item.name,
      });
      this.displayFieldDialog = true;
    },
    openDuplicateFieldDialog(item) {
      this.selectedItem = JSON.parse(JSON.stringify(item));
      this.selectedItem.name = "";
      this.dialogTitle = this.t(
        "adminClassificationPage.dialog.duplicateField",
        {
          name: item.name,
        },
      );
      this.displayFieldDialog = true;
    },
    onFieldSaved(itemData) {
      const index = this.planContent.FIELDS.findIndex(
        (i) => i.name === itemData.name,
      );
      if (index !== -1) {
        this.planContent.FIELDS[index] = itemData;
      } else {
        this.planContent.FIELDS.push(itemData);
      }
      this.displayFieldDialog = false;
    },
    confirmDeleteField(item) {
      const isUsed = this.isFieldUsed(item.name);
      if (isUsed) {
        this.$toast.add({
          severity: "error",
          summary: this.t("adminClassificationPage.toast.deleteImpossible"),
          detail: this.t("adminClassificationPage.toast.fieldUsed", {
            name: item.name,
          }),
          life: 5000,
        });
        return;
      }
      this.confirmDeleteItem(
        item,
        this.planContent.FIELDS,
        this.t("adminClassificationPage.confirm.field"),
      );
    },

    // Emplacement Methods
    openNewEmplacementDialog() {
      this.selectedItem = null;
      this.dialogTitle = this.t(
        "adminClassificationPage.dialog.createEmplacement",
      );
      this.displayEmplacementDialog = true;
    },
    openEditEmplacementDialog(item) {
      this.selectedItem = item;
      this.dialogTitle = this.t(
        "adminClassificationPage.dialog.editEmplacement",
        {
          code: item.code,
        },
      );
      this.displayEmplacementDialog = true;
    },
    onEmplacementSaved(itemData) {
      const index = this.planContent.EMPLACEMENT.findIndex(
        (i) => i.code === itemData.code,
      );
      if (index !== -1) {
        this.planContent.EMPLACEMENT[index] = itemData;
      } else {
        this.planContent.EMPLACEMENT.push(itemData);
      }
      this.autocreateFieldsFromEmplacement(itemData);
      this.displayEmplacementDialog = false;
    },
    confirmDeleteEmplacement(item) {
      const isUsed = this.planContent.NATURES.some((n) =>
        n.emplacement.includes(item.code),
      );
      if (isUsed) {
        this.$toast.add({
          severity: "error",
          summary: this.t("adminClassificationPage.toast.deleteImpossible"),
          detail: this.t("adminClassificationPage.toast.emplacementUsed", {
            code: item.code,
          }),
          life: 5000,
        });
        return;
      }
      this.confirmDeleteItem(
        item,
        this.planContent.EMPLACEMENT,
        this.t("adminClassificationPage.confirm.emplacement"),
      );
    },

    // Category Methods
    openNewCategoryDialog() {
      this.categoryForm = { id: "", label: "", display: [], views: [] };
      this.isEditingCategory = false;
      this.dialogTitle = this.t(
        "adminClassificationPage.dialog.createCategory",
      );
      this.displayCategoryDialog = true;
    },
    openEditCategoryDialog(cat) {
      this.categoryForm = {
        id: cat.id,
        label: cat.label,
        display: [...(cat.display || [])],
        views: [...(cat.views || [])],
      };
      this.isEditingCategory = true;
      this.dialogTitle = this.t("adminClassificationPage.dialog.editCategory", {
        label: cat.label,
      });
      this.displayCategoryDialog = true;
    },
    onCategoryReorder(event) {
      this.planContent.VIEW_CATEGORIES = event.value;
    },
    saveCategoryForm() {
      if (!this.planContent.VIEW_CATEGORIES) {
        this.planContent.VIEW_CATEGORIES = [];
      }
      const index = this.planContent.VIEW_CATEGORIES.findIndex(
        (c) => c.id === this.categoryForm.id,
      );
      const catData = { ...this.categoryForm };
      if (index !== -1) {
        this.planContent.VIEW_CATEGORIES[index] = catData;
      } else {
        this.planContent.VIEW_CATEGORIES.push(catData);
      }
      this.displayCategoryDialog = false;
    },
    openViewSelector(cat) {
      this.viewSelectorCategory = cat;
      this.displayViewSelector = true;
    },
    onViewsUpdated(views) {
      if (this.viewSelectorCategory) {
        this.viewSelectorCategory.views = views;
      }
    },
    confirmDeleteCategory(cat) {
      this.confirm.require({
        message: this.t("adminClassificationPage.confirm.deleteMessage", {
          itemType: this.t("adminClassificationPage.confirm.category"),
          name: cat.label,
        }),
        header: this.t("adminClassificationPage.confirm.deleteHeader"),
        icon: "pi pi-exclamation-triangle",
        group: "deleteItem",
        acceptProps: { severity: "danger" },
        accept: () => {
          const index = this.planContent.VIEW_CATEGORIES.findIndex(
            (c) => c.id === cat.id,
          );
          if (index > -1) {
            this.planContent.VIEW_CATEGORIES.splice(index, 1);
          }
          this.$toast.add({
            severity: "info",
            summary: this.t("adminClassificationPage.toast.confirmed"),
            detail: this.t("adminClassificationPage.toast.itemDeleted"),
            life: 3000,
          });
        },
      });
    },

    // Dictionary Methods
    openNewDictionaryDialog() {
      this.selectedItem = null;
      this.dialogTitle = this.t(
        "adminClassificationPage.dialog.createDictionary",
      );
      this.displayDictionaryDialog = true;
    },
    openEditDictionaryDialog(item) {
      this.selectedItem = item;
      this.dialogTitle = this.t(
        "adminClassificationPage.dialog.editDictionary",
        { label: item.label },
      );
      this.displayDictionaryDialog = true;
    },
    onDictionarySaved(itemData) {
      if (!this.planContent.DICTIONARIES) {
        this.planContent.DICTIONARIES = [];
      }
      const index = this.planContent.DICTIONARIES.findIndex(
        (d) => d.id === itemData.id,
      );
      if (index !== -1) {
        this.planContent.DICTIONARIES[index] = itemData;
      } else {
        this.planContent.DICTIONARIES.push(itemData);
      }
      this.displayDictionaryDialog = false;
    },
    confirmDeleteDictionary(item) {
      const isUsed = this.isDictionaryOrLexiconUsed(item.id);
      if (isUsed) {
        this.$toast.add({
          severity: "error",
          summary: this.t("adminClassificationPage.toast.deleteImpossible"),
          detail: this.t("adminClassificationPage.toast.sourceUsed", {
            id: item.id,
          }),
          life: 5000,
        });
        return;
      }
      this.confirmDeleteItem(
        item,
        this.planContent.DICTIONARIES,
        this.t("adminClassificationPage.confirm.dictionary"),
      );
    },

    // Lexicon Methods
    openNewLexiconDialog() {
      this.selectedItem = null;
      this.dialogTitle = this.t("adminClassificationPage.dialog.createLexicon");
      this.displayLexiconDialog = true;
    },
    openEditLexiconDialog(item) {
      this.selectedItem = item;
      this.dialogTitle = this.t("adminClassificationPage.dialog.editLexicon", {
        label: item.label,
      });
      this.displayLexiconDialog = true;
    },
    onLexiconSaved(itemData) {
      if (!this.planContent.LEXICONS) {
        this.planContent.LEXICONS = [];
      }
      const index = this.planContent.LEXICONS.findIndex(
        (l) => l.id === itemData.id,
      );
      if (index !== -1) {
        this.planContent.LEXICONS[index] = itemData;
      } else {
        this.planContent.LEXICONS.push(itemData);
      }
      this.displayLexiconDialog = false;
    },
    confirmDeleteLexicon(item) {
      const isUsed = this.isDictionaryOrLexiconUsed(item.id);
      if (isUsed) {
        this.$toast.add({
          severity: "error",
          summary: this.t("adminClassificationPage.toast.deleteImpossible"),
          detail: this.t("adminClassificationPage.toast.sourceUsed", {
            id: item.id,
          }),
          life: 5000,
        });
        return;
      }
      this.confirmDeleteItem(
        item,
        this.planContent.LEXICONS,
        this.t("adminClassificationPage.confirm.lexicon"),
      );
    },

    isDictionaryOrLexiconUsed(sourceId) {
      return (this.planContent.FIELDS || []).some((f) => f.source === sourceId);
    },

    // Auto-creation and Deletion Checks
    autocreateFieldsFromNature(nature) {
      const requiredFieldSet = new Set();

      Object.values(nature.metadata).forEach((valueObj) => {
        if (valueObj.field) {
          requiredFieldSet.add(valueObj.field);
        }
        if (valueObj.template) {
          extractVariables(valueObj.template).forEach((v) =>
            requiredFieldSet.add(v),
          );
        }
      });

      nature.emplacement.forEach((code) => {
        const emp = this.planContent.EMPLACEMENT.find((e) => e.code === code);
        if (emp) {
          extractVariables(emp.name).forEach((v) => requiredFieldSet.add(v));
          extractVariables(emp.title).forEach((v) => requiredFieldSet.add(v));
        }
      });

      requiredFieldSet.forEach((fieldName) => {
        if (!this.planContent.FIELDS.some((f) => f.name === fieldName)) {
          this.planContent.FIELDS.push({
            name: fieldName,
            description: this.t(
              "adminClassificationPage.toast.autoCreatedForNature",
              { name: nature.name },
            ),
            type: "string",
            input: "text",
          });
          this.$toast.add({
            severity: "info",
            summary: this.t("adminClassificationPage.toast.fieldCreated"),
            detail: this.t("adminClassificationPage.toast.fieldAutoCreated", {
              fieldName,
            }),
            life: 4000,
          });
        }
      });
    },

    autocreateFieldsFromEmplacement(emplacement) {
      const requiredFieldSet = new Set();

      extractVariables(emplacement.name).forEach((v) =>
        requiredFieldSet.add(v),
      );
      extractVariables(emplacement.title).forEach((v) =>
        requiredFieldSet.add(v),
      );

      requiredFieldSet.forEach((fieldName) => {
        if (!this.planContent.FIELDS.some((f) => f.name === fieldName)) {
          this.planContent.FIELDS.push({
            name: fieldName,
            description: this.t(
              "adminClassificationPage.toast.autoCreatedForEmplacement",
              { code: emplacement.code },
            ),
            type: "string",
            input: "text",
          });
          this.$toast.add({
            severity: "info",
            summary: this.t("adminClassificationPage.toast.fieldCreated"),
            detail: this.t("adminClassificationPage.toast.fieldAutoCreated", {
              fieldName,
            }),
            life: 4000,
          });
        }
      });
    },

    isFieldUsed(fieldName) {
      for (const nature of this.planContent.NATURES) {
        for (const meta of Object.values(nature.metadata)) {
          if (meta.field === fieldName) {
            return true;
          }
          if (
            meta.template &&
            extractVariables(meta.template).includes(fieldName)
          ) {
            return true;
          }
        }
      }

      for (const emp of this.planContent.EMPLACEMENT) {
        if (extractVariables(emp.name).includes(fieldName)) {
          return true;
        }
        if (extractVariables(emp.title).includes(fieldName)) {
          return true;
        }
      }

      return false;
    },

    // Generic Delete
    confirmDeleteItem(item, collection, itemType) {
      this.confirm.require({
        message: this.t("adminClassificationPage.confirm.deleteMessage", {
          itemType,
          name: item.name || item.label || item.code,
        }),
        header: this.t("adminClassificationPage.confirm.deleteHeader"),
        icon: "pi pi-exclamation-triangle",
        group: "deleteItem",
        acceptProps: { severity: "danger" },
        accept: () => {
          const key = item.id ? "id" : item.code ? "code" : "name";
          const index = collection.findIndex((i) => i[key] === item[key]);
          if (index > -1) {
            collection.splice(index, 1);
          }
          this.$toast.add({
            severity: "info",
            summary: this.t("adminClassificationPage.toast.confirmed"),
            detail: this.t("adminClassificationPage.toast.itemDeleted"),
            life: 3000,
          });
        },
      });
    },
  },
  mounted() {
    if (!this.filename.startsWith("plan-")) {
      this.$toast.add({
        severity: "warn",
        summary: this.t("adminClassificationPage.toast.invalidFilename"),
        detail: this.t(
          "adminClassificationPage.toast.filenameMustStartWithPlan",
        ),
        life: 5000,
      });
    }
    this.loadPlan();
    this.loadAvailableViews();
    window.addEventListener("beforeunload", this.handleBeforeUnload);
  },
  beforeUnmount() {
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
  },
  watch: {
    filename() {
      this.loadPlan();
    },
    planContent: {
      handler() {
        if (!this.loading) {
          // Do not mark as dirty on initial load
          this.isDirty = true;
        }
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.category-form-field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.category-form-label {
  font-size: 0.75rem;
  color: var(--p-text-muted-color, #6b7280);
  font-weight: 600;
}
</style>

<style>
.p-datatable .p-datatable-filter-constraint {
  width: 100%;
}
</style>
